import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import api, { API_BASE } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import RegionBlocked from "@/components/RegionBlocked";
import { LockBadge, LockedNotice, AccessModal } from "@/components/LockedContent";
import { useSeo } from "@/lib/useSeo";

const toAbs = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`;
  return url;
};

export default function HealingEntry() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [modal, setModal] = useState(false);

  useSeo({ title: entry?.title, description: entry?.excerpt, image: entry?.cover_image, type: "article" });

  useEffect(() => {
    setLoading(true);
    api.get(`/healing/${slug}`)
      .then(({ data }) => setEntry(data))
      .catch((e) => {
        if (e.response?.status === 451) setBlocked(true);
        else if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PublicLayout><div className="pt-40 pb-32 text-center text-[#5C6670]">Loading…</div></PublicLayout>;
  if (blocked) return <RegionBlocked />;
  if (notFound || !entry) {
    return (
      <PublicLayout>
        <div className="pt-40 pb-32 text-center">
          <p className="font-serif text-3xl italic text-[#5C6670]">This space has drifted out to sea.</p>
          <Link to="/healing" className="mt-6 inline-block gv-link text-sm tracking-[0.2em] uppercase text-[#C5A059]">Back to the space</Link>
        </div>
      </PublicLayout>
    );
  }

  const locked = entry.locked_preview;

  return (
    <PublicLayout>
      <AccessModal open={modal} onClose={() => setModal(false)} />
      <article data-testid="healing-entry-page" className="pt-36 pb-32 px-6 md:px-12 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link to="/healing" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]" data-testid="healing-back">← Space for Healing Together</Link>
            <div className="mt-8 flex items-center gap-3">
              {entry.locked && <LockBadge testId="healing-entry-lock" />}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl mt-5 text-[#1A1F24] leading-[1.1]">{entry.title}</h1>
            {entry.excerpt && <p className="mt-6 text-[#5C6670] text-lg italic font-light max-w-2xl">{entry.excerpt}</p>}
          </motion.div>
        </div>

        {entry.cover_image && (
          <div className="max-w-5xl mx-auto mt-14">
            <img src={entry.cover_image} alt={entry.title} className="w-full h-[380px] md:h-[520px] object-cover" />
          </div>
        )}

        <div className="max-w-2xl mx-auto mt-14">
          {locked ? (
            <LockedNotice onRequest={() => setModal(true)} label="This is exclusive healing content" />
          ) : (
            <div className="space-y-10" data-testid="healing-unlocked">
              {entry.audio_url && (
                <div>
                  <div className="gv-label mb-2">Listen</div>
                  <audio controls src={toAbs(entry.audio_url)} className="w-full" data-testid="healing-audio" />
                </div>
              )}
              {entry.video_url && (
                <div>
                  <div className="gv-label mb-2">Watch</div>
                  <video controls src={toAbs(entry.video_url)} className="w-full rounded" data-testid="healing-video" />
                </div>
              )}
              {entry.content && (
                <div className="gv-prose" data-testid="healing-content">
                  {/<[a-z][\s\S]*>/i.test(entry.content)
                    ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.content) }} />
                    : entry.content.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="max-w-2xl mx-auto mt-20 pt-12 border-t border-[#E5E5E5] text-center">
          <p className="font-serif italic text-xl text-[#5C6670]">— with love, Gemma</p>
          <Link to="/healing" className="mt-8 inline-block gv-link text-sm tracking-[0.2em] uppercase text-[#C5A059]">More in this space</Link>
        </div>
      </article>
    </PublicLayout>
  );
}
